import {
  MEETING_ERROR_CODES,
  MEETING_ERROR_MESSAGES,
  type MeetingErrorCode,
} from '../constants/meetings.constants'

/**
 * Typed error thrown by the meetings service / repo. Consumers branch on
 * `.code` and pick a message from MEETING_ERROR_MESSAGES (or override).
 */
export class MeetingError extends Error {
  readonly code: MeetingErrorCode
  readonly field?: string

  constructor(code: MeetingErrorCode, message?: string, field?: string) {
    super(message ?? MEETING_ERROR_MESSAGES[code])
    this.name = 'MeetingError'
    this.code = code
    this.field = field
  }
}

export const isMeetingError = (err: unknown): err is MeetingError =>
  err instanceof MeetingError

export const toMeetingError = (err: unknown): MeetingError => {
  if (isMeetingError(err)) return err
  return new MeetingError(MEETING_ERROR_CODES.UNKNOWN)
}

/**
 * Field-level validation errors collected during form validation. Keyed by
 * form field name (`title`, `name`, `meetingId`, `time`, etc.) so dialogs
 * can render inline messages without re-encoding strings.
 */
export type ValidationErrors = Record<string, string>

export class MeetingValidationError extends Error {
  readonly errors: ValidationErrors
  constructor(errors: ValidationErrors) {
    super('Validation failed')
    this.name = 'MeetingValidationError'
    this.errors = errors
  }
}

export const isValidationError = (
  err: unknown,
): err is MeetingValidationError => err instanceof MeetingValidationError
